/* eslint-disable react-hooks/rules-of-hooks */
import React from "react";
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  useWindowDimensions,
} from "react-native";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import RenderHtml from "react-native-render-html";
import { useTheme } from "../../../../theme";

interface DynamicTrainingContentProps {
  contentId: string;
  isSubmitSuccess?: boolean;
}

// Helper function to process HTML content
const processHtmlContent = (htmlContent: string): string => {
  if (!htmlContent) {
    return "";
  }

  // Replace escaped HTML entities
  let content = htmlContent
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");

  // Replace escaped newlines
  content = content.replace(/\\n/g, "\n");

  // Convert <br> tags to proper HTML line breaks if they aren't already
  content = content.replace(/\\r\\n/g, "<br />").replace(/\r\n/g, "<br />");

  // Remove any special characters or extra spaces
  content = content.replace(/&#58;/g, ":").replace(/&#160;/g, " ");

  // Remove any remaining quotation marks that may be part of the escaped content
  content = content.replace(/\\"/g, '"');

  // Ensure proper spacing between paragraphs by wrapping text in paragraph tags if needed
  if (!content.includes("<p>")) {
    // Split by double newlines and wrap each in paragraph tags
    const paragraphs = content.split("\n\n").filter((p) => p.trim() !== "");
    if (paragraphs.length > 1) {
      content = paragraphs.map((p) => `<p>${p}</p>`).join("");
    }
  }

  return content;
};

// Helper to map content IDs to Title patterns from the API
const getContentByTitlePattern = (contents: any[], contentId: string) => {
  const titlePatterns: { [key: string]: string } = {
    "safety-training": "Safety Training",
    "pit-training": "PIT Training",
    "vp-equipment": "VIP Equipment",
    "metrics-explained": "Metrics Explained",
    "performance-expectations": "Performance Expectation",
    "management-commitment": "Management Commitment",
  };

  const pattern = titlePatterns[contentId] || contentId;

  // Log all available content titles for debugging
  console.log(
    `Searching for pattern: "${pattern}" among titles:`,
    contents.map((item) => item.Title || "No Title")
  );

  // Try exact match first
  let matchedContent = contents.find(
    (item: any) =>
      item.Title && item.Title.toLowerCase() === pattern.toLowerCase()
  );

  // If no exact match, try includes
  if (!matchedContent) {
    matchedContent = contents.find(
      (item: any) =>
        item.Title && item.Title.toLowerCase().includes(pattern.toLowerCase())
    );
  }

  // If still no match, try more flexible matching
  if (!matchedContent && pattern === "Management Commitment") {
    matchedContent = contents.find(
      (item: any) =>
        item.Title &&
        (item.Title.toLowerCase().includes("management") ||
          item.Title.toLowerCase().includes("commitment") ||
          item.Title.toLowerCase().includes("manager"))
    );
  }

  // Log the match result
  if (matchedContent) {
    console.log(`Found matching content: "${matchedContent.Title}"`);
  } else {
    console.log(`No matching content found for: "${pattern}"`);
  }

  return matchedContent;
};

export const DynamicTrainingContent: React.FC<DynamicTrainingContentProps> = ({
  contentId,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  const { trainingContents, isLoading, error } = useSelector(
    (state: RootState) => state.employeeProfile
  );
  // Get employee data from Redux store
  const { firstName, lastName } = useSelector(
    (state: RootState) => state.employeeUpdates
  );
  // Get manager username from auth slice
  const { user } = useSelector((state: RootState) => state.auth);

  // Format manager name from auth slice
  const formatManagerName = (name: string | undefined): string => {
    if (!name) {
      return "Manager";
    }

    // Handle format like "Mohammed, Sohail - Contractor {}"
    if (name.includes(",")) {
      const parts = name.split(",");
      if (parts.length >= 2) {
        const lastName = parts[0].trim();
        // Remove anything after a dash or curly brace
        const firstNamePart = parts[1].split(/[-{]/)[0].trim();
        return `${firstNamePart} ${lastName}`;
      }
    }

    // Handle other formats or return as is if no comma
    return name.split(/[-{]/)[0].trim(); // Remove anything after dash or curly brace
  };

  const managerName = formatManagerName(user?.displayName || user?.name);

  // Log available content for debugging
  React.useEffect(() => {
    if (trainingContents && trainingContents.length > 0) {
      console.log(
        "Available training content titles:",
        trainingContents.map((item: any) => item.Title)
      );
    }
  }, [trainingContents]);

  // Find the content by matching contentId with Title pattern
  const contentItem = getContentByTitlePattern(trainingContents, contentId);

  if (isLoading) {
    return (
      <View style={[styles.contentWrapper, styles.loadingContainer]}>
        <ActivityIndicator
          size="large"
          color={isDarkTheme ? colors.text.primary : "#002366"}
        />
        <Text
          style={[
            styles.loadingText,
            isDarkTheme && { color: colors.text.primary },
          ]}
        >
          Loading training content...
        </Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.contentWrapper}>
        <Text style={[styles.errorText, isDarkTheme && { color: colors.text.primary }]}>
          Failed to load training content: {error}
        </Text>
      </View>
    );
  }

  if (!contentItem) {
    console.log("Content ID not found:", contentId);
    console.log(
      "Available title patterns:",
      trainingContents.map((item: any) => ({
        title: item.Title,
        matchTest: item.Title ? item.Title.toLowerCase() : "no-title",
      }))
    );

    // For Management Commitment, provide default content if API content is not found
    if (contentId === "management-commitment") {
      const { width } = useWindowDimensions();
      const defaultContent =
        firstName && lastName
          ? `<p>I, <b>${managerName}</b> commit to providing clear expectations and weekly touch points with <b>${firstName} ${lastName}</b> along with 30/60/90 day holistic performance reviews.</p>`
          : "<p>Management commitment information not available.</p>";

      return (
        <View style={styles.contentWrapper}>
          {/* Add success message if form submitted successfully */}
          {/* {isSubmitSuccess && (
            <View style={styles.successMessageContainer}>
              <Text style={styles.successMessageText}>✓ Training completed successfully</Text>
            </View>
          )} */}

          <Text style={[styles.header, isDarkTheme && { color: colors.text.primary }]}>Management Commitment</Text>
          <RenderHtml
            contentWidth={width - 32}
            source={{ html: defaultContent }}
            tagsStyles={{
              p: {
                ...styles.paragraph,
                color: isDarkTheme ? colors.text.primary : "black",
              },
              b: {
                fontWeight: "bold",
                color: isDarkTheme ? colors.text.primary : "black",
              },
            }}
          />
        </View>
      );
    }

    return (
      <View style={styles.contentWrapper}>
        <Text style={[styles.errorText, isDarkTheme && { color: colors.text.primary }]}>Training content not found.</Text>
      </View>
    );
  }

  // Process the HTML content from MessageText field
  const processedHtml = processHtmlContent(contentItem.MessageText);

  // Check if this is the Management Commitment content
  const isManagementCommitment = contentId === "management-commitment";

  // Get window width for RenderHtml
  const { width } = useWindowDimensions();

  return (
    <View style={styles.contentWrapper}>
      {/* Add success message if form submitted successfully
      {isSubmitSuccess && (
        <View style={styles.successMessageContainer}>
          <Text style={styles.successMessageText}>✓ Training completed successfully</Text>
        </View>
      )} */}

      {/* Add certification text for all content except Management Commitment */}
      {!isManagementCommitment && firstName && lastName && (
        <Text
          style={[
            styles.certificationText,
            isDarkTheme && { color: colors.text.primary },
          ]}
        >
          I, {firstName} {lastName} certify that I have been trained on and have
          a functional understanding of the following:
        </Text>
      )}

      <RenderHtml
        contentWidth={width - 32} // Adjust based on your padding/margins
        source={{ html: processedHtml }}
        tagsStyles={{
          p: {
            ...styles.paragraph,
            color: isDarkTheme ? colors.text.primary : "black",
          },
          b: {
            fontWeight: "bold",
            color: isDarkTheme ? colors.text.primary : "black",
          },
          strong: {
            fontWeight: "bold",
            color: isDarkTheme ? colors.text.primary : "black",
          },
          li: {
            ...styles.listItem,
            color: isDarkTheme ? colors.text.primary : "black",
          },
          ul: styles.listContainer,
          ol: { ...styles.listContainer, marginLeft: 20 },
          h1: {
            ...styles.listHeader,
            fontSize: 20,
            color: isDarkTheme ? colors.text.primary : "#002366",
          },
          h2: {
            ...styles.listHeader,
            fontSize: 18,
            color: isDarkTheme ? colors.text.primary : "#002366",
          },
          h3: {
            ...styles.subHeader,
            color: isDarkTheme ? colors.text.primary : "#002366",
          },
          body: {
            lineHeight: 22,
            fontSize: 16,
            color: isDarkTheme ? colors.text.primary : "black",
          },
        }}
      />
    </View>
  );
};

// Kept for backward compatibility, will be populated with dynamic content
export const SafetyTrainingContent = ({
  isSubmitSuccess,
}: {
  isSubmitSuccess?: boolean;
}) => (
  <DynamicTrainingContent
    contentId="safety-training"
    isSubmitSuccess={isSubmitSuccess}
  />
);

export const PitTrainingContent = ({
  isSubmitSuccess,
}: {
  isSubmitSuccess?: boolean;
}) => (
  <DynamicTrainingContent
    contentId="pit-training"
    isSubmitSuccess={isSubmitSuccess}
  />
);

export const VPEquipmentContent = ({
  isSubmitSuccess,
}: {
  isSubmitSuccess?: boolean;
}) => (
  <DynamicTrainingContent
    contentId="vp-equipment"
    isSubmitSuccess={isSubmitSuccess}
  />
);

export const MetricsExplainedContent = ({
  isSubmitSuccess,
}: {
  isSubmitSuccess?: boolean;
}) => (
  <DynamicTrainingContent
    contentId="metrics-explained"
    isSubmitSuccess={isSubmitSuccess}
  />
);

export const PerformanceExpectationContent = ({
  isSubmitSuccess,
}: {
  isSubmitSuccess?: boolean;
}) => (
  <DynamicTrainingContent
    contentId="performance-expectations"
    isSubmitSuccess={isSubmitSuccess}
  />
);

export const ManagementCommitmentContent = ({
  isSubmitSuccess,
}: {
  isSubmitSuccess?: boolean;
}) => (
  <DynamicTrainingContent
    contentId="management-commitment"
    isSubmitSuccess={isSubmitSuccess}
  />
);

const styles = StyleSheet.create({
  contentWrapper: {
    paddingBottom: 20,
    paddingHorizontal: 16, // Add horizontal padding for proper content alignment
  },
  header: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 15,
    color: "#002366",
  },
  successMessageContainer: {
    backgroundColor: "#e6f7e6",
    padding: 10,
    borderRadius: 5,
    marginBottom: 15,
    borderLeftWidth: 4,
    borderLeftColor: "#4CAF50",
    flexDirection: "row",
    alignItems: "center",
  },
  successMessageText: {
    color: "#2E7D32",
    fontWeight: "bold",
    fontSize: 16,
  },
  certificationText: {
    fontSize: 16,
    marginBottom: 20,
    lineHeight: 22,
    color: "black",
  },
  paragraph: {
    fontSize: 16,
    marginBottom: 15,
    lineHeight: 22,
    color: "black", // Ensure text is readable
  },
  listContainer: {
    marginLeft: 10,
    marginBottom: 15,
  },
  listHeader: {
    fontSize: 18,
    fontWeight: "bold",
    marginTop: 15,
    marginBottom: 5,
    color: "#002366",
  },
  subHeader: {
    fontSize: 16,
    fontWeight: "bold",
    marginTop: 10,
    marginBottom: 5,
    color: "#002366",
  },
  listItem: {
    fontSize: 16,
    marginBottom: 5,
    paddingLeft: 5,
    lineHeight: 22,
  },
  loadingContainer: {
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: "#002366",
  },
  errorText: {
    color: "#dc3545",
    fontSize: 16,
    marginBottom: 15,
  },
});

export default {
  SafetyTrainingContent,
  PitTrainingContent,
  VPEquipmentContent,
  MetricsExplainedContent,
  PerformanceExpectationContent,
  ManagementCommitmentContent,
  DynamicTrainingContent,
};
